import { apiClient } from '@/app/core/services/apiClient.service';
import { BaseApiService } from '@/app/shared/services/apis/baseApi.service';
import type { ID } from '@fifa-bsm/bo-frontend-shared/utils/types';
import type { ApiResponse } from '@/app/shared/services/apis/types/ApiResponse';
import type { ETicketingContactDetails } from '@/app/shared/services/apis/types/eTicketing/ETicketingContactDetails';
import type { CancelTicketsDistributionPayload } from '@/app/shared/services/apis/types/eTicketing/payloads';

class SecutixDistributionApi extends BaseApiService {
    distributePhysicalTickets(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/distribute`,
            payload
        );
    }

    distributeTournamentAppTickets(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/tournament-app/distribute`,
            payload
        );
    }

    distributeETickets(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/distribute-etickets`,
            payload
        );
    }

    distributeETicketsToGroup(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/distribute-etickets-to-group`,
            payload
        );
    }

    cancelDistributions(mainEventId: ID, eventId: ID, payload: CancelTicketsDistributionPayload) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/cancel-distributions`,
            payload
        );
    }
}

class TicketsDistributionApiService extends BaseApiService {
    #secutix: SecutixDistributionApi;

    constructor(_apiClient: typeof apiClient, secutixDistributionApi: SecutixDistributionApi) {
        super(_apiClient);
        this.#secutix = secutixDistributionApi;
    }

    forSecutix() {
        return this.#secutix;
    }

    distributeTickets(mainEventId: ID, eventId: ID, ticketsIds: number[]) {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/tickets/secutix/distribute`,
            {
                tickets: ticketsIds,
            }
        );
    }

    distributeETickets(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.put(
            `main-events/${mainEventId}/events/${eventId}/eticketing/distribute-etickets`,
            payload
        );
    }

    distributeETicketsToGroup(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.put(
            `main-events/${mainEventId}/events/${eventId}/eticketing/distribute-etickets-to-group`,
            payload
        );
    }

    getETicketingContactDetails(mainEventId: ID, eventId: ID, guestIds: number[]) {
        return this.client.get<ApiResponse<ETicketingContactDetails[]>>(
            `guest-operations/eticketing/main-events/${mainEventId}/events/${eventId}/guest-contact-details`,
            { guests: guestIds }
        );
    }

    cancelETicketsDistribution(
        mainEventId: ID,
        eventId: ID,
        payload: CancelTicketsDistributionPayload
    ) {
        return this.client.patch(
            `main-events/${mainEventId}/events/${eventId}/eticketing/cancel-distribution`,
            payload
        );
    }

    undoETicketsDistribution(mainEventId: ID, eventId: ID, payload: any) {
        return this.client.patch(
            `main-events/${mainEventId}/events/${eventId}/eticketing/undo-distribution`,
            payload
        );
    }

    cancelDistribution(eventId: ID, ticketId: ID) {
        return this.client.post(
            `seat-allocator/guest-operations/allocation-list/events/${eventId}/tickets/${ticketId}/cancel-distribution`
        );
    }

    undoDistribution(eventId: ID, ticketId: ID) {
        return this.client.post(
            `seat-allocator/guest-operations/allocation-list/events/${eventId}/tickets/${ticketId}/undo-distribution`
        );
    }

    setTicketCheckIn(eventId: ID, payload: { ticket_ids: ID[]; checked_in: boolean }) {
        return this.client.post(
            `seat-allocator/guest-operations/allocation-list/events/${eventId}/tickets/check-in`,
            payload
        );
    }

    async getEmailsForWhichETicketDistributionOccurred(
        mainEventId: ID,
        eventId: ID,
        ticketIds: number[]
    ) {
        return this.client.get(
            `main-events/${mainEventId}/events/${eventId}/eticketing/distribution-details`,
            { tickets: ticketIds }
        );
    }
}

export const ticketsDistributionApi = new TicketsDistributionApiService(
    apiClient,
    new SecutixDistributionApi(apiClient)
);
